import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { ChevronDown, Sparkles, X, UserPlus, Pencil } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import api from "../services/api";
import TagInput from "./TagInput";
import TagSuggestionsModal from "./TagSuggestionsModal";
import "./ModalFormShared.css";
import "./NewCandidateModal.css";

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  linkedin_url: string;
  headline: string;
  short_code: string;
  summary: string;
  main_skill: string;
  skills: string[];
  tags: string[];
  seniority: string;
  role_type: string;
  location: string;
  last_company: string;
  english_level: string;
  notice_period: string;
  availability_date: string;
  rate_min: string;
  rate_max: string;
  source: string;
  recommended_by: string;
  responsible_recruiter_id: string;
  personality_type: string;
  door_opener: boolean;
  resume_url: string;
  blind_cv_word_url: string;
  blind_cv_pdf_url: string;
}

interface FormErrors {
  [key: string]: string;
}

interface User {
  id: number;
  username: string;
  email: string;
}

interface UsersResponse {
  users?: User[];
}

interface TagsResponse {
  success?: boolean;
  tags?: { tag: string; reason?: string }[];
}

interface NewCandidateModalProps {
  onSave: () => void;
  onCancel: () => void;
}

const INITIAL_FORM_DATA: FormData = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  linkedin_url: "",
  headline: "",
  short_code: "",
  summary: "",
  main_skill: "",
  skills: [],
  tags: [],
  seniority: "",
  role_type: "",
  location: "",
  last_company: "",
  english_level: "",
  notice_period: "",
  availability_date: "",
  rate_min: "",
  rate_max: "",
  source: "",
  recommended_by: "",
  responsible_recruiter_id: "",
  personality_type: "",
  door_opener: false,
  resume_url: "",
  blind_cv_word_url: "",
  blind_cv_pdf_url: "",
};

const NewCandidateModal: React.FC<NewCandidateModalProps> = ({
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({ ...INITIAL_FORM_DATA });
  const [errors, setErrors] = useState<FormErrors>({});
  const [shortCodeManual, setShortCodeManual] = useState(false);
  const [tagInputValue, setTagInputValue] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(["availability", "documents"]),
  );
  const [tagSuggestions, setTagSuggestions] = useState<
    { tag: string; reason?: string }[]
  >([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);

  // Fetch recruiter users
  const { data: recruitersData } = useQuery<UsersResponse>(
    ["users", "recruiter"],
    () =>
      recruitingAPI.getUsersByRole(
        "recruiter",
      ) as unknown as Promise<UsersResponse>,
    { staleTime: 5 * 60 * 1000 },
  );

  const recruiters = recruitersData?.users || [];

  // Auto-generate short code from name
  useEffect(() => {
    if (shortCodeManual) return;
    const f = formData.first_name.replace(/[^a-zA-Z]/g, "");
    const l = formData.last_name.replace(/[^a-zA-Z]/g, "");
    if (f.length >= 2 && l.length >= 2) {
      const code =
        f.charAt(0).toUpperCase() +
        f.charAt(1).toLowerCase() +
        l.charAt(0).toUpperCase() +
        l.charAt(1).toLowerCase();
      setFormData((prev) => ({ ...prev, short_code: code }));
    } else {
      setFormData((prev) => ({ ...prev, short_code: "" }));
    }
  }, [formData.first_name, formData.last_name, shortCodeManual]);

  // Create mutation
  const createMutation = useMutation<unknown, Error, Record<string, unknown>>(
    (data) => recruitingAPI.createCandidate(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates");
        onSave();
      },
      onError: (error: Error) => {
        console.error("Error creating candidate:", error);
        setErrors({
          general: t("candidates.newModal.validation.saveFailed"),
        });
      },
    },
  );

  // AI mutations
  const suggestSummaryMutation = useMutation<
    { success: boolean; data: { summary: string } },
    Error
  >(
    () => recruitingAPI.suggestCandidateSummary(getProfileContext()),
    {
      onSuccess: (response) => {
        if (response.data?.summary) {
          setFormData((prev) => ({ ...prev, summary: response.data.summary }));
        } else {
          setErrors((prev) => ({
            ...prev,
            summary: t("candidates.newModal.validation.aiSuggestionFailed"),
          }));
        }
      },
      onError: (error: Error) => {
        console.error("Error suggesting summary:", error);
        setErrors((prev) => ({
          ...prev,
          summary: t("candidates.newModal.validation.aiSuggestionFailed"),
        }));
      },
    },
  );

  const suggestMainSkillMutation = useMutation<
    { success: boolean; data: { main_skill: string } },
    Error
  >(
    () => recruitingAPI.suggestCandidateMainSkill(getProfileContext()),
    {
      onSuccess: (response) => {
        if (response.data?.main_skill) {
          setFormData((prev) => ({
            ...prev,
            main_skill: response.data.main_skill,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            main_skill: t("candidates.newModal.validation.aiSuggestionFailed"),
          }));
        }
      },
      onError: (error: Error) => {
        console.error("Error suggesting main skill:", error);
        setErrors((prev) => ({
          ...prev,
          main_skill: t("candidates.newModal.validation.aiSuggestionFailed"),
        }));
      },
    },
  );

  const suggestSkillsMutation = useMutation<
    { success: boolean; data: { skills: string[] } },
    Error
  >(
    () => recruitingAPI.suggestCandidateSkills(getProfileContext()),
    {
      onSuccess: (response) => {
        if (response.data?.skills?.length) {
          setFormData((prev) => {
            const incoming = response.data.skills.filter(
              (s) => !prev.skills.includes(s),
            );
            return { ...prev, skills: [...prev.skills, ...incoming] };
          });
        } else {
          setErrors((prev) => ({
            ...prev,
            skills: t("candidates.newModal.validation.aiSuggestionFailed"),
          }));
        }
      },
      onError: (error: Error) => {
        console.error("Error suggesting skills:", error);
        setErrors((prev) => ({
          ...prev,
          skills: t("candidates.newModal.validation.aiSuggestionFailed"),
        }));
      },
    },
  );

  const getProfileContext = () => ({
    first_name: formData.first_name,
    last_name: formData.last_name,
    headline: formData.headline,
    summary: formData.summary,
    main_skill: formData.main_skill,
    skills: formData.skills,
    seniority: formData.seniority,
    role_type: formData.role_type,
    location: formData.location,
    english_level: formData.english_level,
    notice_period: formData.notice_period,
    rate_min: formData.rate_min,
    rate_max: formData.rate_max,
    source: formData.source,
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleGenerateTags = async () => {
    setIsGeneratingTags(true);
    setErrors((prev) => ({ ...prev, tags: "" }));

    try {
      const content = {
        name: `${formData.first_name} ${formData.last_name}`,
        main_skill: formData.main_skill,
        seniority: formData.seniority,
        role_type: formData.role_type,
        location: formData.location,
        summary: formData.summary,
        skills: formData.skills,
      };

      const response = (await api.openai.generateTags(
        JSON.stringify(content),
        "candidate",
        formData.tags,
      )) as TagsResponse;

      if (response.success && response.tags && response.tags.length > 0) {
        setTagSuggestions(response.tags);
        setShowTagSuggestions(true);
      } else {
        setErrors((prev) => ({
          ...prev,
          tags: t("candidates.newModal.validation.noTagSuggestions"),
        }));
      }
    } catch (error) {
      console.error("Error generating tags:", error);
      setErrors((prev) => ({
        ...prev,
        tags: t("candidates.newModal.validation.tagsFailed"),
      }));
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleAddSuggestedTags = (selectedTags: string[]) => {
    const newTags = [...formData.tags, ...selectedTags];
    const uniqueTags = [...new Set(newTags)];
    setFormData((prev) => ({ ...prev, tags: uniqueTags }));
    setTagSuggestions([]);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const value = tagInputValue.trim();
      if (value && !formData.tags.includes(value)) {
        setFormData((prev) => ({ ...prev, tags: [...prev.tags, value] }));
      }
      setTagInputValue("");
    }
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const isCollapsed = (sectionId: string) => collapsedSections.has(sectionId);

  const getSectionSummary = (sectionId: string): string => {
    const values: string[] = [];
    if (sectionId === "professional") {
      if (formData.seniority) values.push(formData.seniority);
      if (formData.role_type) values.push(formData.role_type);
      if (formData.location) values.push(formData.location);
      if (formData.last_company) values.push(formData.last_company);
    } else if (sectionId === "availability") {
      if (formData.notice_period) values.push(formData.notice_period);
      if (formData.rate_min)
        { const rateRange = formData.rate_max ? `${formData.rate_min}-${formData.rate_max}` : `${formData.rate_min}`; values.push(`${rateRange} EUR/hr`); }
      if (formData.availability_date) values.push(formData.availability_date);
    } else if (sectionId === "contact") {
      if (formData.first_name || formData.last_name)
        values.push(`${formData.first_name} ${formData.last_name}`.trim());
      if (formData.email) values.push(formData.email);
    } else if (sectionId === "documents") {
      if (formData.personality_type) values.push(formData.personality_type);
      if (formData.door_opener) values.push("Door opener");
      if (formData.resume_url || formData.blind_cv_word_url || formData.blind_cv_pdf_url)
        values.push("CV attached");
    }
    return values.join(", ");
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = t("candidates.newModal.validation.firstNameRequired");
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = t("candidates.newModal.validation.lastNameRequired");
    }
    if (!formData.email.trim()) {
      newErrors.email = t("candidates.newModal.validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("candidates.newModal.validation.emailInvalid");
    }
    if (
      formData.rate_min &&
      Number.isNaN(Number.parseFloat(formData.rate_min))
    ) {
      newErrors.rate_min = t("candidates.newModal.validation.rateMinNumber");
    }
    if (
      formData.rate_max &&
      Number.isNaN(Number.parseFloat(formData.rate_max))
    ) {
      newErrors.rate_max = t("candidates.newModal.validation.rateMaxNumber");
    }
    if (
      formData.rate_min &&
      formData.rate_max &&
      !newErrors.rate_min &&
      !newErrors.rate_max &&
      Number.parseFloat(formData.rate_min) > Number.parseFloat(formData.rate_max)
    ) {
      newErrors.rate_max = t("candidates.newModal.validation.rateMaxBelowMin");
    }
    if (formData.linkedin_url && !formData.linkedin_url.startsWith("http")) {
      newErrors.linkedin_url = t(
        "candidates.newModal.validation.linkedInUrlInvalid",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: Record<string, unknown> = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || null,
      linkedin_url: formData.linkedin_url || null,
      headline: formData.headline || null,
      short_code: formData.short_code || null,
      summary: formData.summary || null,
      main_skill: formData.main_skill || null,
      seniority: formData.seniority || null,
      role_type: formData.role_type || null,
      location: formData.location || null,
      last_company: formData.last_company || null,
      english_level: formData.english_level || null,
      notice_period: formData.notice_period || null,
      availability_date: formData.availability_date || null,
      rate_min: formData.rate_min
        ? Number.parseFloat(formData.rate_min)
        : null,
      rate_max: formData.rate_max
        ? Number.parseFloat(formData.rate_max)
        : null,
      source: formData.source || null,
      recommended_by: formData.recommended_by || null,
      responsible_recruiter_id: formData.responsible_recruiter_id
        ? Number.parseInt(formData.responsible_recruiter_id, 10)
        : null,
      personality_type: formData.personality_type || null,
      door_opener: formData.door_opener,
      resume_url: formData.resume_url || null,
      blind_cv_word_url: formData.blind_cv_word_url || null,
      blind_cv_pdf_url: formData.blind_cv_pdf_url || null,
      tags: formData.tags,
      skills_data:
        formData.skills.length > 0
          ? JSON.stringify(
              formData.skills.reduce(
                (acc: Record<string, number>, skill) => {
                  acc[skill] = 50;
                  return acc;
                },
                {},
              ),
            )
          : null,
    };

    createMutation.mutate(submitData);
  };

  const renderAiButton = (
    onClick: () => void,
    isLoading: boolean,
    title: string,
  ) => (
    <button
      type="button"
      className={`btn-ai-icon ${isLoading ? "loading" : ""}`}
      onClick={onClick}
      disabled={isLoading}
      title={isLoading ? t("candidates.newModal.ai.thinking") : title}
    >
      {isLoading ? <span className="ai-spinner" /> : <Sparkles size={14} />}
    </button>
  );

  const seniorityOptions = [
    { value: "Junior", label: t("candidates.newModal.seniority.junior") },
    { value: "Mid", label: t("candidates.newModal.seniority.mid") },
    { value: "Senior", label: t("candidates.newModal.seniority.senior") },
    { value: "Lead", label: t("candidates.newModal.seniority.lead") },
    { value: "Principal", label: t("candidates.newModal.seniority.principal") },
  ];

  const englishLevelOptions = [
    { value: "A1", label: t("candidates.newModal.englishLevel.a1") },
    { value: "A2", label: t("candidates.newModal.englishLevel.a2") },
    { value: "B1", label: t("candidates.newModal.englishLevel.b1") },
    { value: "B2", label: t("candidates.newModal.englishLevel.b2") },
    { value: "C1", label: t("candidates.newModal.englishLevel.c1") },
    { value: "C2", label: t("candidates.newModal.englishLevel.c2") },
  ];

  const noticePeriodOptions = [
    { value: "Immediate", label: t("candidates.newModal.noticePeriod.immediate") },
    { value: "1 week", label: t("candidates.newModal.noticePeriod.oneWeek") },
    { value: "2 weeks", label: t("candidates.newModal.noticePeriod.twoWeeks") },
    { value: "1 month", label: t("candidates.newModal.noticePeriod.oneMonth") },
    { value: "2 months", label: t("candidates.newModal.noticePeriod.twoMonths") },
    { value: "3 months", label: t("candidates.newModal.noticePeriod.threeMonths") },
  ];

  const sourceOptions = [
    { value: "linkedin", label: t("candidates.newModal.source.linkedin") },
    { value: "job-board", label: t("candidates.newModal.source.jobBoard") },
    { value: "referral", label: t("candidates.newModal.source.referral") },
    { value: "recruiter", label: t("candidates.newModal.source.recruiter") },
    { value: "direct-application", label: t("candidates.newModal.source.directApplication") },
    { value: "networking", label: t("candidates.newModal.source.networking") },
    { value: "other", label: t("candidates.newModal.source.other") },
  ];

  const personalityTypeOptions = [
    { value: "Extrovert", label: t("candidates.newModal.personalityType.extrovert") },
    { value: "Introvert", label: t("candidates.newModal.personalityType.introvert") },
    { value: "Ambivert", label: t("candidates.newModal.personalityType.ambivert") },
  ];

  const renderSelect = (
    name: keyof FormData,
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
    hint?: string,
  ) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select
        name={name}
        className="form-select"
        value={formData[name] as string}
        onChange={handleInputChange}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hint && <span className="form-hint">{hint}</span>}
    </div>
  );

  const renderCollapsibleHeader = (sectionId: string, label: string) => {
    const collapsed = isCollapsed(sectionId);
    const summary = collapsed ? getSectionSummary(sectionId) : "";
    return (
      <button
        type="button"
        className={`panel-section-header ${collapsed ? "collapsed" : ""}`}
        onClick={() => toggleSection(sectionId)}
        aria-expanded={!collapsed}
        aria-controls={`section-content-${sectionId}`}
      >
        <span className="panel-section-label">{label}</span>
        {summary && <span className="panel-section-summary">{summary}</span>}
        <ChevronDown size={16} className="panel-section-chevron" />
      </button>
    );
  };

  return (
    <div className="candidate-modal modal-form">
      {/* Header */}
      <div className="modal-form-header">
        <div className="candidate-modal-header-left">
          <h2>{t("candidates.newModal.title")}</h2>
          {formData.short_code && (
            <span className="candidate-id-badge">{formData.short_code}</span>
          )}
        </div>
        <button
          type="button"
          className="modal-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="modal-form-inner">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        <div className="modal-form-body">
          {/* LEFT PANEL — Profile */}
          <div className="panel-left">
            {/* Short Code + Headline */}
            <div className="form-row-shortcode">
              <div className="form-group">
                <label className="form-label">
                  {t("candidates.newModal.fields.shortCode")}
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    name="short_code"
                    className={`form-input ${shortCodeManual ? "" : "form-input-readonly"}`}
                    value={formData.short_code}
                    onChange={handleInputChange}
                    readOnly={!shortCodeManual}
                  />
                  <button
                    type="button"
                    className="input-icon-right"
                    onClick={() => setShortCodeManual(true)}
                    title="Edit short code"
                  >
                    <Pencil size={14} />
                  </button>
                </div>
                <span className="form-hint">
                  {t("candidates.newModal.hints.autoGenerated")}
                </span>
              </div>
              <div className="form-group">
                <label className="form-label">
                  {t("candidates.newModal.fields.headline")}
                </label>
                <input
                  type="text"
                  name="headline"
                  className="form-input"
                  value={formData.headline}
                  onChange={handleInputChange}
                  placeholder={t("candidates.newModal.placeholders.headline")}
                />
              </div>
            </div>

            {/* Summary section */}
            <div className="left-section-divider">
              <span className="left-section-label">
                {t("candidates.newModal.sections.summary")}
              </span>
            </div>

            <div className="form-group">
              <div className="label-row">
                <label className="form-label">
                  {t("candidates.newModal.fields.candidateSummary")}
                </label>
                {renderAiButton(
                  () => suggestSummaryMutation.mutate(),
                  suggestSummaryMutation.isLoading,
                  t("candidates.newModal.ai.suggestSummary"),
                )}
              </div>
              <textarea
                name="summary"
                className={`form-textarea ${errors.summary ? "error" : ""}`}
                value={formData.summary}
                onChange={handleInputChange}
                rows={8}
                placeholder={t("candidates.newModal.placeholders.summary")}
              />
              {errors.summary && (
                <span className="error-text">{errors.summary}</span>
              )}
            </div>

            {/* Skills section */}
            <div className="left-section-divider">
              <span className="left-section-label">
                {t("candidates.newModal.sections.skills")}
              </span>
            </div>

            <div className="form-group">
              <div className="label-row">
                <label className="form-label">
                  {t("candidates.newModal.fields.mainSkill")}
                </label>
                {renderAiButton(
                  () => suggestMainSkillMutation.mutate(),
                  suggestMainSkillMutation.isLoading,
                  t("candidates.newModal.ai.suggestMainSkill"),
                )}
              </div>
              <input
                type="text"
                name="main_skill"
                className="form-input"
                value={formData.main_skill}
                onChange={handleInputChange}
                placeholder={t("candidates.newModal.placeholders.mainSkill")}
              />
              {errors.main_skill && (
                <span className="error-text">{errors.main_skill}</span>
              )}
            </div>

            <div className="form-group">
              <div className="label-row">
                <label className="form-label">
                  {t("candidates.newModal.fields.skills")}
                </label>
                {renderAiButton(
                  () => suggestSkillsMutation.mutate(),
                  suggestSkillsMutation.isLoading,
                  t("candidates.newModal.ai.suggestSkills"),
                )}
              </div>
              <TagInput
                value={formData.skills}
                onChange={(skills: string[]) =>
                  setFormData((prev) => ({ ...prev, skills }))
                }
                placeholder={t("candidates.newModal.placeholders.addSkill")}
              />
              {errors.skills && (
                <span className="error-text">{errors.skills}</span>
              )}
            </div>

            <div className="form-group">
              <div className="label-row">
                <label className="form-label" htmlFor="candidate-tag-input">
                  {t("candidates.newModal.fields.tags")}
                </label>
                {renderAiButton(
                  handleGenerateTags,
                  isGeneratingTags,
                  t("candidates.newModal.ai.suggestTags"),
                )}
              </div>
              <div className="tag-input-container">
                {formData.tags.map((tag) => (
                  <span key={tag} className="tag-pill">
                    {tag}
                    <button
                      type="button"
                      className="tag-remove"
                      onClick={() => handleRemoveTag(tag)}
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  id="candidate-tag-input"
                  type="text"
                  className="tag-input"
                  placeholder={t("candidates.newModal.placeholders.addTag")}
                  value={tagInputValue}
                  onChange={(e) => setTagInputValue(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                />
              </div>
              {errors.tags && (
                <span className="error-text">{errors.tags}</span>
              )}
            </div>
          </div>

          {/* RIGHT PANEL — Enrich */}
          <div className="panel-right">
            {/* Professional Details (expanded) */}
            {renderCollapsibleHeader(
              "professional",
              t("candidates.newModal.sections.professionalDetails"),
            )}
            <div
              id="section-content-professional"
              className={`panel-section-content ${isCollapsed("professional") ? "collapsed" : ""}`}
            >
              <div className="form-row-2col">
                {renderSelect(
                  "seniority",
                  t("candidates.newModal.fields.seniority"),
                  t("candidates.newModal.selects.selectLevel"),
                  seniorityOptions,
                )}
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.roleType")}
                  </label>
                  <input
                    type="text"
                    name="role_type"
                    className="form-input"
                    value={formData.role_type}
                    onChange={handleInputChange}
                    placeholder={t("candidates.newModal.placeholders.roleType")}
                  />
                </div>
              </div>

              <div className="form-row-2col">
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.location")}
                  </label>
                  <input
                    type="text"
                    name="location"
                    className="form-input"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder={t("candidates.newModal.placeholders.location")}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.lastCompany")}
                  </label>
                  <input
                    type="text"
                    name="last_company"
                    className="form-input"
                    value={formData.last_company}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.lastCompany",
                    )}
                  />
                </div>
              </div>

              {renderSelect(
                "english_level",
                t("candidates.newModal.fields.englishLevel"),
                t("candidates.newModal.selects.selectEnglishLevel"),
                englishLevelOptions,
              )}
            </div>

            {/* Availability & Rate (collapsed) */}
            {renderCollapsibleHeader(
              "availability",
              t("candidates.newModal.sections.availabilityRate"),
            )}
            <div
              id="section-content-availability"
              className={`panel-section-content ${isCollapsed("availability") ? "collapsed" : ""}`}
            >
              <div className="form-row-2col">
                {renderSelect(
                  "notice_period",
                  t("candidates.newModal.fields.noticePeriod"),
                  t("candidates.newModal.selects.selectPeriod"),
                  noticePeriodOptions,
                )}
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.availabilityDate")}
                  </label>
                  <input
                    type="date"
                    name="availability_date"
                    className="form-input"
                    value={formData.availability_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t("candidates.newModal.fields.expectedRate")}
                </label>
                <div className="form-row-2col">
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="rate_min"
                      className={`form-input ${errors.rate_min ? "error" : ""}`}
                      value={formData.rate_min}
                      onChange={handleInputChange}
                      placeholder="210"
                      step="0.01"
                    />
                    <span className="input-suffix">&euro;/hr</span>
                  </div>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="rate_max"
                      className={`form-input ${errors.rate_max ? "error" : ""}`}
                      value={formData.rate_max}
                      onChange={handleInputChange}
                      placeholder="230"
                      step="0.01"
                    />
                    <span className="input-suffix">&euro;/hr</span>
                  </div>
                </div>
                <div className="form-row-2col" style={{ marginTop: "-4px" }}>
                  <span className="form-hint">
                    {t("candidates.newModal.hints.rateMin")}
                  </span>
                  <span className="form-hint">
                    {t("candidates.newModal.hints.rateMax")}
                  </span>
                </div>
                {errors.rate_min && (
                  <span className="error-text">{errors.rate_min}</span>
                )}
                {errors.rate_max && (
                  <span className="error-text">{errors.rate_max}</span>
                )}
              </div>
            </div>

            {/* Contact & Source (expanded) */}
            {renderCollapsibleHeader(
              "contact",
              t("candidates.newModal.sections.contactSource"),
            )}
            <div
              id="section-content-contact"
              className={`panel-section-content ${isCollapsed("contact") ? "collapsed" : ""}`}
            >
              <div className="form-row-2col">
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.firstName")}{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    className={`form-input ${errors.first_name ? "error" : ""}`}
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.firstName",
                    )}
                  />
                  {errors.first_name && (
                    <span className="error-text">{errors.first_name}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.lastName")}{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    className={`form-input ${errors.last_name ? "error" : ""}`}
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.lastName",
                    )}
                  />
                  {errors.last_name && (
                    <span className="error-text">{errors.last_name}</span>
                  )}
                </div>
              </div>

              <div className="form-row-2col">
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.email")}{" "}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("candidates.newModal.placeholders.email")}
                  />
                  {errors.email && (
                    <span className="error-text">{errors.email}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.phone")}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t("candidates.newModal.placeholders.phone")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t("candidates.newModal.fields.linkedIn")}
                </label>
                <input
                  type="url"
                  name="linkedin_url"
                  className={`form-input ${errors.linkedin_url ? "error" : ""}`}
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder={t("candidates.newModal.placeholders.linkedIn")}
                />
                {errors.linkedin_url && (
                  <span className="error-text">{errors.linkedin_url}</span>
                )}
              </div>

              <div className="form-row-2col">
                {renderSelect(
                  "source",
                  t("candidates.newModal.fields.source"),
                  t("candidates.newModal.selects.selectSource"),
                  sourceOptions,
                )}
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.recommendedBy")}
                  </label>
                  <input
                    type="text"
                    name="recommended_by"
                    className="form-input"
                    value={formData.recommended_by}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.recommendedBy",
                    )}
                  />
                </div>
              </div>

              {renderSelect(
                "responsible_recruiter_id",
                t("candidates.newModal.fields.responsibleRecruiter"),
                t("candidates.newModal.selects.selectRecruiter"),
                recruiters.map((user) => ({
                  value: user.id.toString(),
                  label: user.username,
                })),
                t("candidates.newModal.hints.recruiterDefault"),
              )}
            </div>

            {/* Documents & Profile (collapsed) */}
            {renderCollapsibleHeader(
              "documents",
              t("candidates.newModal.sections.documentsProfile"),
            )}
            <div
              id="section-content-documents"
              className={`panel-section-content ${isCollapsed("documents") ? "collapsed" : ""}`}
            >
              <div className="form-row-2col">
                {renderSelect(
                  "personality_type",
                  t("candidates.newModal.fields.personalityType"),
                  t("candidates.newModal.selects.selectType"),
                  personalityTypeOptions,
                )}
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.doorOpener")}
                  </label>
                  <div className="toggle-wrapper" style={{ marginTop: "2px" }}>
                    <label className="toggle-switch" aria-label={t("candidates.newModal.fields.doorOpener")}>
                      <input
                        type="checkbox"
                        checked={formData.door_opener}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            door_opener: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider" />
                    </label>
                    <span className="toggle-label">
                      {formData.door_opener ? t("common:misc.yes") : t("common:misc.no")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t("candidates.newModal.fields.resumeUrl")}
                </label>
                <input
                  type="url"
                  name="resume_url"
                  className="form-input"
                  value={formData.resume_url}
                  onChange={handleInputChange}
                  placeholder={t("candidates.newModal.placeholders.resumeUrl")}
                />
              </div>

              <div className="form-row-2col">
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.blindCvWord")}
                  </label>
                  <input
                    type="url"
                    name="blind_cv_word_url"
                    className="form-input"
                    value={formData.blind_cv_word_url}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.blindCvUrl",
                    )}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t("candidates.newModal.fields.blindCvPdf")}
                  </label>
                  <input
                    type="url"
                    name="blind_cv_pdf_url"
                    className="form-input"
                    value={formData.blind_cv_pdf_url}
                    onChange={handleInputChange}
                    placeholder={t(
                      "candidates.newModal.placeholders.blindCvUrl",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-form-footer">
          <button type="button" onClick={onCancel} className="btn-cancel">
            {t("candidates.newModal.buttons.cancel")}
          </button>
          <button
            type="submit"
            className="btn-create"
            disabled={createMutation.isLoading}
          >
            <UserPlus size={18} />
            {createMutation.isLoading
              ? t("candidates.newModal.buttons.creating")
              : t("candidates.newModal.buttons.create")}
          </button>
        </div>

        <TagSuggestionsModal
          isOpen={showTagSuggestions}
          onClose={() => setShowTagSuggestions(false)}
          suggestions={tagSuggestions}
          onAddTags={handleAddSuggestedTags}
          isLoading={isGeneratingTags}
        />
      </form>
    </div>
  );
};

export default NewCandidateModal;
